/**
 * Billing Setup Wizard
 *
 * Multi-step wizard for configuring claim submission.
 * Steps: Connect Clearinghouse -> Add Identity -> Verify -> Payer Setup
 */

import { useEffect, useRef, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import type { SlideProps } from '@mui/material/Slide';
import { useEventTracking } from '@services/statsig';

import { ConnectStediStep } from './wizard/ConnectStediStep';
import { CreateEntityStep } from './wizard/CreateEntityStep';
import { EnrollPayersStep } from './wizard/EnrollPayersStep';
import { SyncToStediStep } from './wizard/SyncToStediStep';

interface BillingSetupWizardProps {
  open: boolean;
  onClose: () => void;
  /** Optional initial step to start at (0-3) */
  initialStep?: number;
  /** Optional existing entity ID to use (skips entity creation) */
  existingEntityId?: string | null;
}

const SlideTransition = (props: SlideProps) => {
  return <Slide {...props} direction="up" />;
};

export function BillingSetupWizard({
  open,
  onClose,
  initialStep = 0,
  existingEntityId = null,
}: BillingSetupWizardProps) {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [contentKey, setContentKey] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  
  // Track wizard state
  const [stediConnected, setStediConnected] = useState(initialStep > 0);
  const [billingEntityId, setBillingEntityId] = useState<string | null>(existingEntityId);
  const [syncCompleted, setSyncCompleted] = useState(false);

  const { billing } = useEventTracking();
  const flowStartTime = useRef<number>(Date.now());
  const stepStartTime = useRef<number>(Date.now());

  const steps = ['Connect Clearinghouse', 'Add Identity', 'Verify', 'Payer Setup'];
  const maxSteps = 4;
  
  // Update state when props change (e.g., reopening with different params)
  useEffect(() => {
    setActiveStep(initialStep);
    if (existingEntityId) {
      setBillingEntityId(existingEntityId);
    }
  }, [initialStep, existingEntityId]);

  // Track wizard started/abandoned
  useEffect(() => {
    if (open) {
      flowStartTime.current = Date.now();
      stepStartTime.current = Date.now();

      // TODO: Add billing event tracking when implemented
      // billing?.setupWizardStarted?.({
      //   entry_point: 'billing_settings',
      // });
    }

    return () => {
      // Track abandonment if user closes before completing
      if (open && activeStep < maxSteps - 1) {
        // TODO: Add billing event tracking when implemented
        // billing?.setupWizardAbandoned?.({
        //   step_name: steps[activeStep],
        //   step_number: activeStep,
        //   time_in_wizard: Math.round((Date.now() - flowStartTime.current) / 1000),
        // });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll to top when step changes
  useEffect(() => {
    if (open) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep, open]);

  const handleNext = () => {
    if (activeStep < maxSteps - 1) {
      // Track step completion
      // TODO: Add billing event tracking when implemented
      // const timeOnStep = Math.round((Date.now() - stepStartTime.current) / 1000);
      // billing?.setupWizardStepCompleted?.({
      //   step_name: steps[activeStep],
      //   step_number: activeStep,
      //   time_on_step: timeOnStep,
      // });

      setSlideDirection('left');
      setActiveStep(prev => prev + 1);
      setContentKey(prev => prev + 1);
      stepStartTime.current = Date.now();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      // TODO: Add billing event tracking when implemented
      // billing?.setupWizardStepBack?.({
      //   step_name: steps[activeStep],
      //   step_number: activeStep,
      // });

      setSlideDirection('right');
      setActiveStep(prev => prev - 1);
      setContentKey(prev => prev + 1);
      stepStartTime.current = Date.now();
    }
  };

  const handleComplete = () => {
    // Track wizard completion
    // TODO: Add billing event tracking when implemented
    // const totalTime = Math.round((Date.now() - flowStartTime.current) / 1000);
    // billing?.setupWizardCompleted?.({
    //   total_time: totalTime,
    //   billing_entity_created: billingEntityId !== null,
    //   sync_completed: syncCompleted,
    // });

    onClose();
  };

  const handleClose = () => {
    // TODO: Add billing event tracking when implemented
    // if (activeStep < maxSteps - 1) {
    //   // Track abandonment
    //   billing?.setupWizardAbandoned?.({
    //     step_name: steps[activeStep],
    //     step_number: activeStep,
    //     time_in_wizard: Math.round((Date.now() - flowStartTime.current) / 1000),
    //   });
    // }
    onClose();
  };

  const getStepContent = (step: number): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <ConnectStediStep
            onNext={handleNext}
            onConnected={() => setStediConnected(true)}
          />
        );
      case 1:
        return (
          <CreateEntityStep
            onNext={handleNext}
            onBack={handleBack}
            onEntityCreated={(entityId: string) => setBillingEntityId(entityId)}
            existingEntityId={billingEntityId}
          />
        );
      case 2:
        return (
          <SyncToStediStep
            billingEntityId={billingEntityId}
            onNext={handleNext}
            onBack={handleBack}
            onSyncCompleted={() => setSyncCompleted(true)}
          />
        );
      case 3:
        return (
          <EnrollPayersStep
            billingEntityId={billingEntityId}
            onBack={handleBack}
            onComplete={handleComplete}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={SlideTransition}
      keepMounted={false}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Claim Submission Setup</span>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content with Slide Transition */}
        <Slide direction={slideDirection} in={true} mountOnEnter unmountOnExit key={contentKey}>
          <Box sx={{ minHeight: 300 }}>{getStepContent(activeStep)}</Box>
        </Slide>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
